

"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Container } from "@/components/layout/container";

const categories = [
  {
    title: "Pelotes uniques",
    description:
      "Des pièces singulières, teintes à la main par mes soins, pensées pour donner du caractère et de l’émotion à vos ouvrages.",
      href: "/boutique?category=pelotes-uniques",
    tag: "Unique",
    image: "/images/4.webp",
  },
  {
    title: "Kits 2 pelotes",
    description:
      "Des associations harmonieuses, imaginées pour créer un bel équilibre de nuances, de contraste et de douceur.",
      href: "/boutique?category=kits-2-pelotes",
    tag: "Duo",
    image: "/images/5.webp",
  },
  {
    title: "Kits 3 pelotes",
    description:
      "Des compositions plus riches pour explorer la couleur, construire une palette et commencer une œuvre plus ambitieuse.",
      href: "/boutique?category=kits-3-pelotes",
    tag: "Trio",
    image: "/images/6.webp",
  },
];

const values = [
  {
    title: "Teint à la main",
    text: "Chaque kit et chaque pelote sont teints artisanalement par mes soins, dans une approche sensible de la couleur et de la matière.",
  },
  {
    title: "Disponibles à la vente",
    text: "Tous les kits proposés sur ma boutique sont disponibles, prêts à rejoindre votre maison et votre prochain projet.",
  },
  {
    title: "Pensés pour créer",
    text: "Je compose mes laines comme des accords visuels : pour inspirer, donner envie et accompagner le début d’une œuvre.",
  },
];

const editorialLinks = [
  {
    title: "Qui suis-je",
    text: "Découvrir mon parcours, mon univers et la sensibilité qui guide mes créations.",
    href: "/qui-suis-je",
  },
  {
    title: "Mes sources d’inspiration",
    text: "Explorer les couleurs, les ambiances et les références qui nourrissent mon travail.",
    href: "/sources-d-inspiration",
  },
  {
    title: "Mon travail de teinturière",
    text: "Comprendre mon geste, ma méthode et l’attention portée à chaque teinture.",
    href: "/travail-de-teinturiere",
  },
  {
    title: "Conseils d’entretien",
    text: "Retrouver les bons gestes pour préserver la beauté et la tenue de vos laines.",
    href: "/conseils-d-entretien",
  },
];

const galleryImages = [
  {
    src: "/images/7.webp",
    alt: "Palette de laines teintes à la main Jour J Création",
    ratio: "aspect-[4/5]",
    delay: 0,
  },
  {
    src: "/images/8.webp",
    alt: "Détail de pelotes de laine artisanales",
    ratio: "aspect-[4/3]",
    delay: 90,
  },
  {
    src: "/images/9.webp",
    alt: "Composition visuelle de laines teintes à la main",
    ratio: "aspect-[4/5]",
    delay: 160,
  },
];
const bodyFont = { fontFamily: '"Inter", sans-serif' };
const monoFont = { fontFamily: '"IBM Plex Mono", monospace' };
const homePalette = {
  "--primary": "#d10f73",
  "--accent": "#d95a2f",
  "--foreground": "#111111",
  "--secondary": "#d95a2f",
  "--background": "#f4d7e7",
  "--card": "linear-gradient(145deg, rgba(209,15,115,0.10), rgba(217,90,47,0.08))",
  "--muted": "rgba(217,90,47,0.10)",
  "--border": "rgba(17,17,17,0.10)",
} as React.CSSProperties;

function Reveal({
  children,
  delay = 0,
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.16,
        rootMargin: "0px 0px -8% 0px",
      }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0px)" : "translateY(32px)",
        transition: `opacity 760ms ease, transform 760ms ease ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

function ImageCard({
  src,
  alt,
  className = "",
  priority = false,
  sizes = "(max-width: 768px) 100vw, 50vw",
}: {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  sizes?: string;
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-[28px] border border-[var(--border)] bg-[linear-gradient(145deg,rgba(209,15,115,0.10),rgba(217,90,47,0.08))] shadow-[var(--shadow)] ${className}`}
    >
      <Image
        src={src}
        alt={alt}
        fill
        priority={priority}
        sizes={sizes}
        className="object-cover transition duration-700 ease-out hover:scale-[1.03]"
      />
    </div>
  );
}

export default function HomePage() {
  return (
    <div
      style={{
        ...bodyFont,
        ...homePalette,
        background:
          "linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)",
      }}
    >
      <HeroSection />
      <IntroSection />
      <CategoriesSection />
      <CompositionSection />
      <GallerySection />
      <CareSection />
      <EditorialSection />
      <ClosingSection />
    </div>
  );
}

function HeroSection() {
  return (
    <section className="section overflow-hidden pt-8 sm:pt-10 lg:pt-12">
      <Container>
        <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:gap-14">
          <Reveal className="max-w-[760px]">
            <p
              style={monoFont}
              className="text-[11px] uppercase tracking-[0.24em] text-[var(--accent)]/90"
            >
              Laine teinte à la main · univers sensible
            </p>

            <h1 className="mt-4 max-w-[10ch] text-5xl leading-[0.9] sm:text-6xl xl:text-7xl">
              Des laines teintes comme des fragments de couleur.
            </h1>

            <p className="mt-6 max-w-[31rem] text-base leading-7 text-[var(--foreground)]/76 sm:text-lg">
              Pelotes uniques et kits teints à la main, pensés comme des compositions délicates,
              chaleureuses et vivantes.
            </p>

            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                href="/boutique"
                className="rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
              >
                Découvrir la collection
              </Link>

              <Link
                href="/qui-suis-je"
                className="rounded-full border border-[rgba(217,90,47,0.22)] bg-[rgba(217,90,47,0.12)] px-6 py-3 text-sm font-medium text-[var(--foreground)] transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
              >
                Entrer dans l’univers
              </Link>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="relative mx-auto w-full max-w-[560px]">
              <div className="relative aspect-[4/4.7] overflow-hidden rounded-[28px] shadow-[var(--shadow)]">
                <Image
                  src="/images/5.webp"
                  alt="Univers Jour J Création"
                  fill
                  priority
                  sizes="(max-width: 1024px) 100vw, 42vw"
                  className="object-cover object-center"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(209,15,115,0.08)_0%,rgba(255,255,255,0)_28%,rgba(217,90,47,0.10)_100%)]" />
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function IntroSection() {
  return (
    <section className="section pt-8 sm:pt-10">
      <Container>
        <Reveal className="mb-8 max-w-2xl">
          <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
            Une approche artisanale
          </p>
          <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
            Une boutique pensée comme un univers de couleur, pas comme un catalogue figé.
          </h2>
        </Reveal>

        <div className="grid gap-6 md:grid-cols-3">
          {values.map((item, index) => (
            <Reveal key={item.title} delay={index * 90}>
              <article className="rounded-[28px] border border-[var(--border)] bg-[var(--card)] p-6 shadow-[var(--shadow)]">
                <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]">
                  Ma boutique
                </p>
                <h2 className="mt-4 text-2xl leading-tight">{item.title}</h2>
                <p className="mt-4 text-sm leading-6 text-[var(--foreground)]/75">
                  {item.text}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function CategoriesSection() {
  return (
    <section className="section">
      <Container>
        <Reveal className="mb-10 max-w-2xl">
          <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
            Les catégories
          </p>
          <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
            Trois façons de choisir vos laines selon vos envies de création.
          </h2>
          <p className="mt-5 text-base leading-7 text-[var(--foreground)]/74">
            J’ai structuré ma boutique autour de trois familles principales pour vous
            permettre de naviguer simplement entre pelotes uniques, duos et kits plus généreux.
          </p>
        </Reveal>

        <div className="grid gap-6 lg:grid-cols-3">
          {categories.map((item, index) => (
            <Reveal key={item.title} delay={index * 100}>
              <Link
                href={item.href}
                className="group block overflow-hidden rounded-[30px] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow)] transition duration-300 hover:-translate-y-1"
              >
                <div className="relative aspect-[4/4.5] overflow-hidden">
                  <Image
                    src={item.image}
                    alt={item.title}
                    fill
                    sizes="(max-width: 1024px) 100vw, 33vw"
                    className="object-cover transition duration-700 ease-out group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(20,14,18,0.58),rgba(20,14,18,0.04)_55%)]" />


                  <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                    <h3 className="text-2xl text-white sm:text-[1.8rem]">{item.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-white/82">{item.description}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal delay={160} className="mt-8 max-w-xl">
          <div className="border-l border-[var(--accent)]/24 pl-4 sm:pl-5">
            <p style={monoFont} className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent)]/72">
              Note
            </p>
            <p className="mt-3 text-sm leading-6 text-[var(--foreground)]/68">
              Certaines laines contiennent du lurex argenté ou de la stellina argentée
              ou dorée : elles sont alors dites “à paillettes”. Vous pourrez ainsi
              filtrer facilement les créations selon cet éclat particulier.
            </p>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function CompositionSection() {
  return (
    <section className="section">
      <Container>
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <Reveal>
            <ImageCard
              src="/images/2.webp"
              alt="Photo éditoriale de laine teinte à la main"
              className="aspect-[4/5]"
              sizes="(max-width: 1024px) 100vw, 40vw"
            />
          </Reveal>

          <Reveal delay={140} className="max-w-2xl">
            <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
              Composition & fil
            </p>

            <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
              Des bases choisies pour leur qualité, leur tenue et leur beauté au tricot comme au crochet.
            </h2>

            <p className="mt-6 text-base leading-7 text-[var(--foreground)]/74">
              Toutes les laines proposées contiennent du mérinos superwash, entre 75 %
              et 85 %, du nylon entre 10 % et 20 %, et parfois une touche de lurex ou
              de stellina pour apporter un éclat subtil. Le fil est d’épaisseur
              fingering, avec environ 400 à 420 mètres pour 100 grammes.
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="rounded-[28px] border border-[var(--border)] bg-[var(--card)] p-6 shadow-[var(--shadow)]">
                <h3 className="text-lg">Composition</h3>
                <p className="mt-2 text-sm leading-6 text-[var(--foreground)]/72">
                  Mérinos superwash, nylon, et parfois des fibres brillantes pour les versions à paillettes.
                </p>
              </div>

              <div className="rounded-[28px] border border-[var(--border)] bg-[var(--card)] p-6 shadow-[var(--shadow)]">
                <h3 className="text-lg">Épaisseur fingering</h3>
                <p className="mt-2 text-sm leading-6 text-[var(--foreground)]/72">
                  Un fil fin et élégant, pensé pour des ouvrages détaillés, souples et raffinés.
                </p>
              </div>
            </div>

            <div className="mt-8">
              <Link
                href="/specificites-des-produits"
                className="inline-flex rounded-full border border-[rgba(217,90,47,0.22)] bg-[linear-gradient(145deg,rgba(209,15,115,0.16),rgba(217,90,47,0.12))] px-5 py-3 text-sm font-medium transition hover:border-[var(--primary)] hover:text-[var(--primary)]"
              >
                Découvrir les spécificités des produits
              </Link>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function GallerySection() {
  return (
    <section className="section">
      <Container>
        <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <Reveal className="max-w-xl lg:sticky lg:top-[18vh] lg:self-start">
            <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
              Atmosphère
            </p>
            <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
              Une maison de couleur, de matière et de gestes délicats.
            </h2>
            <p className="mt-6 text-base leading-7 text-[var(--foreground)]/74">
              Les photos deviennent ici de vrais éléments d’interface : elles installent le ton,
              donnent de la présence à la matière et renforcent l’identité plus artistique du site.
            </p>
          </Reveal>

          <div className="grid gap-6 sm:grid-cols-2">
            {galleryImages.map((item, index) => (
              <Reveal
                key={item.src}
                delay={item.delay}
                className={index === 1 ? "sm:translate-y-12" : ""}
              >
                <ImageCard
                  src={item.src}
                  alt={item.alt}
                  className={item.ratio}
                  sizes="(max-width: 768px) 100vw, 40vw"
                />
              </Reveal>
            ))}

            <Reveal delay={220} className="sm:translate-y-10">
              <div className="flex h-full min-h-[260px] flex-col justify-between rounded-[28px] border border-[var(--border)] bg-[var(--card)] p-6 shadow-[var(--shadow)] sm:min-h-[320px]">
                <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
                  Note d’atelier
                </p>
                <p className="text-xl leading-8 text-[var(--foreground)]/82 sm:text-2xl">
                  “Choisir une laine, c’est déjà commencer à imaginer le projet.”
                </p>
                <Link
                  href="/sources-d-inspiration"
                  className="inline-flex text-sm font-medium text-[var(--accent)] transition hover:text-[var(--primary)]"
                >
                  Explorer mes inspirations
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

function CareSection() {
  return (
    <section className="section">
      <Container>
        <div className="overflow-hidden rounded-[28px] border border-[var(--border)] bg-[var(--card)] p-8 shadow-[var(--shadow)] sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <Reveal className="max-w-3xl">
              <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
                Entretien
              </p>

              <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
                Même traitées superwash, mes laines méritent un soin délicat.
              </h2>

              <p className="mt-6 text-base leading-7 text-[var(--foreground)]/74">
                Je recommande un lavage à la main, à l’eau froide ou légèrement tiède,
                30 °C maximum. Pour les coloris les plus saturés, un trait de vinaigre blanc
                dans l’eau de rinçage peut être ajouté si besoin.
              </p>

              <div className="mt-8 grid gap-4 md:grid-cols-2">
                <div className="rounded-[28px] border border-[var(--border)] bg-[var(--card)] p-6 shadow-[var(--shadow)]">
                  <h3 className="text-lg">Lavage</h3>
                  <p className="mt-2 text-sm leading-6 text-[var(--foreground)]/72">
                    À la main uniquement, avec douceur, pour préserver les fibres et la profondeur des teintes.
                  </p>
                </div>

                <div className="rounded-[28px] border border-[var(--border)] bg-[var(--card)] p-6 shadow-[var(--shadow)]">
                  <h3 className="text-lg">Séchage</h3>
                  <p className="mt-2 text-sm leading-6 text-[var(--foreground)]/72">
                    À plat, naturellement, sans sèche-linge, pour conserver la forme et la tenue du fil.
                  </p>
                </div>
              </div>

              <div className="mt-8">
                <Link
                  href="/conseils-d-entretien"
                  className="inline-flex rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
                >
                  Voir tous les conseils d’entretien
                </Link>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <ImageCard
                src="/images/8.webp"
                alt="Détail de laine teinte à la main avec texture douce"
                className="aspect-[4/5]"
                sizes="(max-width: 1024px) 100vw, 35vw"
              />
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

function EditorialSection() {
  return (
    <section className="section">
      <Container>
        <Reveal className="mb-10 max-w-2xl">
          <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
            Mon univers
          </p>
          <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
            Des pages pour raconter ma démarche, mes inspirations et mon travail.
          </h2>
        </Reveal>

        <div className="grid gap-6 md:grid-cols-2">
          {editorialLinks.map((item, index) => (
            <Reveal key={item.href} delay={index * 90}>
              <Link
                href={item.href}
                className="group block rounded-[28px] border border-[var(--border)] bg-[var(--card)] p-6 shadow-[var(--shadow)] transition hover:-translate-y-1"
              >
                <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]">
                  Page éditoriale
                </p>
                <h3 className="mt-4 text-2xl">{item.title}</h3>
                <p className="mt-4 text-sm leading-6 text-[var(--foreground)]/74">
                  {item.text}
                </p>
                <span className="mt-6 inline-flex text-sm font-medium text-[var(--accent)] transition group-hover:text-[var(--primary)]">
                  Ouvrir la page
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}
function ClosingSection() {
  return (
    <section className="section pt-0">
      <Container>
        <div className="overflow-hidden rounded-[28px] border border-[var(--border)] bg-[var(--card)] p-8 shadow-[var(--shadow)] sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.95fr] lg:items-center">
            <Reveal className="max-w-3xl">
              <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
                Jour J Création
              </p>

              <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
                Jour J pour commencer votre œuvre d’art.
              </h2>

              <p className="mt-6 max-w-2xl text-base leading-7 text-[var(--foreground)]/74">
                Ma boutique a été pensée comme un espace sensible, chaleureux et vivant,
                où chaque kit, chaque pelote et chaque nuance peuvent devenir le point de
                départ d’une création personnelle.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  href="/boutique"
                  className="rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
                >
                  Accéder à ma boutique
                </Link>

                <Link
                  href="/contact"
                  className="rounded-full border border-[rgba(217,90,47,0.22)] bg-[linear-gradient(145deg,rgba(209,15,115,0.16),rgba(217,90,47,0.12))] px-6 py-3 text-sm font-medium transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
                >
                  Me contacter
                </Link>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <ImageCard
                src="/images/1.webp"
                alt="Photo finale de laines teintes à la main Jour J Création"
                className="aspect-[4/4.6]"
                sizes="(max-width: 1024px) 100vw, 35vw"
              />
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}